import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App, makeRealApp } from "./app/App";
import type { AppCtx } from "./app/AppContext";
import { registerServiceWorker } from "./lib/push";
import "./styles.css";

declare const __WHISTLE_MOCK__: boolean;

/**
 * Mock mode (a fake PC in the tab) exists in dev builds and the dedicated `mock` build mode only (review L3). Both
 * conditions are compile-time constants, so a production build drops this branch and the dynamic import with it:
 * src/mock never ships, and `?mock=1` on the real origin does nothing.
 */
async function mockContext(): Promise<AppCtx | null> {
  const mockBuild = typeof __WHISTLE_MOCK__ !== "undefined" && __WHISTLE_MOCK__;
  if (!mockBuild && !import.meta.env.DEV) return null;
  const { makeMockApp, mockRequested } = await import("./mock/boot");
  let session: Storage | null = null;
  try {
    session = sessionStorage;
  } catch {
    session = null;
  }
  if (!mockBuild && !mockRequested(location.search, session)) return null;
  return makeMockApp();
}

async function boot(): Promise<void> {
  const ctx = (await mockContext()) ?? makeRealApp();
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <App ctx={ctx} />
    </StrictMode>,
  );
  // App sets the boot fallback's MOUNTED_FLAG once its first screen has committed.
  // vite.sw.ts serves /sw.js under `vite` dev as well as in builds. Mock mode has no real PC and no push, so it skips it.
  if (!ctx.mock) void registerServiceWorker();
}

void boot();
